import { useState } from 'react';
import { ArrowSquareOut, DeviceMobile, EnvelopeSimple, Eye, EyeSlash, Key, LockSimple, LockSimpleOpen, Sparkle } from '@phosphor-icons/react';
import { BestaetigenDialog, Chip, ImmoButton, InnenKarte, TextFeld } from '../../../../../components/immobilien/ui';
import { gebePortalFrei, setzePortalStatus } from '../../../../../lib/mieter/portalZugang';
import { passwortFehler, passwortVorschlag } from '../../../../../lib/immobilien/portal';
import type { PortalStatus } from '../../../../../lib/immobilien/einheitDetail';

const CHIP: Record<PortalStatus, { ton: 'neutral' | 'ok' | 'bad' | 'warn'; text: string }> = {
  laedt: { ton: 'neutral', text: 'Wird geprüft …' },
  keinZugang: { ton: 'neutral', text: 'Kein Zugang' },
  aktiv: { ton: 'ok', text: 'Zugang aktiv' },
  gesperrt: { ton: 'bad', text: 'Gesperrt' },
  ohneEmail: { ton: 'warn', text: 'E-Mail fehlt' },
};

/**
 * Mieterportal-Zugang (README §9) im neuen Design – Funktionen wie das bisherige Portal-Formular:
 * Zugang mit Passwort freigeben (Konto + profiles-Eintrag, Rolle mieter, mieter.profile_id), sperren, entsperren.
 * Logik in lib/mieter/portalZugang (gemeinsam mit dem Mieter-Panel). Einladung, App-Code und Berechtigungen fehlen noch.
 */
export function PortalKarte({ mieterName, mieterId, email, status, onGeaendert, onKontakt, zeige }: {
  mieterName: string;
  /** Mieter, dessen profile_id beim Freigeben gesetzt wird */
  mieterId?: string;
  email: string;
  status: PortalStatus;
  onGeaendert: () => void;
  onKontakt: () => void;
  zeige: (text: string, fehler?: boolean) => void;
}) {
  const [formular, setFormular] = useState(false);
  const [passwort, setPasswort] = useState('');
  const [sichtbar, setSichtbar] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [sperren, setSperren] = useState(false);
  const chip = CHIP[status];

  const freigeben = async () => {
    const f = passwortFehler(passwort);
    if (f) { setFehler(f); return; }
    setLaeuft(true); setFehler(null);
    try {
      await gebePortalFrei({ name: mieterName, email, passwort, mieterId });
      setFormular(false); setPasswort('');
      zeige(`Portal-Zugang für ${email} aktiviert`);
      onGeaendert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Zugang konnte nicht angelegt werden');
    } finally {
      setLaeuft(false);
    }
  };

  const setzeStatus = async (neu: 'Aktiv' | 'Inaktiv') => {
    setLaeuft(true);
    const fehler = await setzePortalStatus(email, neu).then(() => null, (e: unknown) => (e instanceof Error ? e.message : 'Fehler'));
    setLaeuft(false);
    setSperren(false);
    if (fehler) { zeige(`Nicht geändert: ${fehler}`, true); return; }
    zeige(neu === 'Inaktiv' ? 'Portal-Zugang gesperrt' : 'Portal-Zugang wieder freigegeben');
    onGeaendert();
  };

  return (
    <InnenKarte>
      <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: 'var(--acc-soft)' }}><DeviceMobile weight="fill" size={20} color="var(--acc)" /></span>
        <div className="flex flex-col flex-1 min-w-0">
          <span style={{ fontSize: 16, fontWeight: 800 }}>Mieterportal-Zugang</span>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>App & Web · Dokumente, Zahlungen, Tickets</span>
        </div>
        <Chip ton={chip.ton} icon={status === 'keinZugang' || status === 'gesperrt' ? LockSimple : undefined}>{chip.text}</Chip>
      </div>
      <span className="flex items-center" style={{ gap: 10, height: 50, padding: '0 14px', borderRadius: 15, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, fontWeight: 700 }}>
        <EnvelopeSimple size={16} color="var(--ink3)" />{email || <span style={{ color: 'var(--ink3)', fontWeight: 500 }}>Keine E-Mail-Adresse hinterlegt</span>}
      </span>

      {status === 'ohneEmail' && (
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Für den Zugang braucht der Mieter eine E-Mail-Adresse (im Kontakt oder im Mietverhältnis ergänzen).</span>
          <ImmoButton icon={ArrowSquareOut} onClick={onKontakt}>Kontakt öffnen</ImmoButton>
        </div>
      )}

      {status === 'keinZugang' && !formular && (
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--ink3)', flex: '1 1 240px' }}>Der Mieter meldet sich mit dieser E-Mail und einem Passwort an, das du vergibst und ihm mitteilst.</span>
          <ImmoButton art="akzent" icon={Key} onClick={() => { setFormular(true); setFehler(null); }}>Zugang freigeben</ImmoButton>
        </div>
      )}

      {status === 'keinZugang' && formular && (
        <div className="flex flex-col" style={{ gap: 10, padding: 12, borderRadius: 18, background: 'var(--fill)' }}>
          <div className="flex items-end flex-wrap" style={{ gap: 8 }}>
            <div style={{ flex: '1 1 220px' }}>
              <TextFeld label="Portal-Passwort" type={sichtbar ? 'text' : 'password'} wert={passwort} onChange={setPasswort} autoComplete="new-password"
                fehler={fehler ?? undefined} placeholder="mindestens 6 Zeichen" />
            </div>
            <ImmoButton groesse={48} pille={false} icon={sichtbar ? EyeSlash : Eye} aria-label={sichtbar ? 'Passwort verbergen' : 'Passwort anzeigen'} onClick={() => setSichtbar(v => !v)} />
            <ImmoButton groesse={48} pille={false} icon={Sparkle} onClick={() => { setPasswort(passwortVorschlag()); setSichtbar(true); setFehler(null); }}>Vorschlagen</ImmoButton>
          </div>
          <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
            <ImmoButton onClick={() => { setFormular(false); setPasswort(''); setFehler(null); }}>Abbrechen</ImmoButton>
            <ImmoButton art="akzent" icon={Key} disabled={laeuft || !passwort} onClick={() => { void freigeben(); }}>{laeuft ? 'Wird angelegt …' : 'Zugang aktivieren'}</ImmoButton>
          </div>
        </div>
      )}

      {status === 'aktiv' && (
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Der Mieter hat Zugang zum Portal.</span>
          <ImmoButton art="bad" icon={LockSimple} disabled={laeuft} onClick={() => setSperren(true)}>Sperren</ImmoButton>
        </div>
      )}
      {status === 'gesperrt' && (
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Der Portal-Zugang ist gesperrt.</span>
          <ImmoButton art="akzent" icon={LockSimpleOpen} disabled={laeuft} onClick={() => { void setzeStatus('Aktiv'); }}>Entsperren</ImmoButton>
        </div>
      )}
      {status !== 'ohneEmail' && (
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Einladung per Link, App-Code, Passwort zurücksetzen und einzelne Berechtigungen werden noch angebunden.</span>
      )}

      {sperren && (
        <BestaetigenDialog titel="Zugang sperren?" text={`${mieterName} kann sich danach nicht mehr im Portal anmelden. Entsperren ist jederzeit möglich.`}
          bestaetigenLabel="Sperren" icon={LockSimple} laeuft={laeuft} onBestaetigen={() => { void setzeStatus('Inaktiv'); }} onAbbrechen={() => setSperren(false)} />
      )}
    </InnenKarte>
  );
}
